// The onboarding path on a counterparty's profile (TX-US-0042): its stages, the state each is in,
// which stage's sheet is open, and what the person looking may do. Pure, so every status and both
// credit review answers are tested without a screen. The API is the authority
// (trex/counterparties/service.py); this decides what is offered, and a control the API would
// refuse is not offered.

import { missingLabels as creditMissing } from '../credit/sheet.js';
import { missingLabels as kycMissing } from '../kyc/sheet.js';
import { GATE_OWNERS } from '../labels.js';

// Read left to right. The last stage names the outcome; the three in between each open a sheet.
export const STAGES = [
  { key: 'prospect', label: 'Prospect' },
  { key: 'kyc', label: 'KYC', sheet: 'KYC review' },
  { key: 'credit', label: 'Credit', sheet: 'Credit assessment' },
  { key: 'legal', label: 'Legal', sheet: 'Legal review' },
  { key: 'outcome', label: 'Approved' },
];

export const SHEET_STAGES = STAGES.filter((stage) => stage.sheet).map((stage) => stage.key);

// The stage a counterparty at a status stands at, and the one a rejection at it marks.
const STAGE_AT = {
  PROSPECT: 'kyc',
  KYC_APPROVED: 'credit',
  LEGAL_REVIEW: 'legal',
  OVERRIDE_REQUESTED: 'legal',
};

const ORDER = STAGES.map((stage) => stage.key);
const before = (a, b) => ORDER.indexOf(a) < ORDER.indexOf(b);

// Compliance answered that Risk need not see it, and Risk has not passed it since.
export const creditSkipped = (record, kycSheet) =>
  !record.credit_approved_by && kycSheet?.credit_review === 'NOT_REQUIRED';

const byException = (record) => record.override?.outcome === 'GRANTED';

// Each stage with its state: done, current, warning (Legal's rejection is referred), skipped, next,
// or rejected; and the label and tooltip it is drawn with.
export function stagesOf(record, kycSheet) {
  const skipped = creditSkipped(record, kycSheet);
  const skipNote = kycSheet?.credit_review_reason
    ? `No credit review: ${kycSheet.credit_review_reason}`
    : 'No credit review';
  const at = record.status === 'REJECTED'
    ? STAGE_AT[record.rejected_from] ?? 'kyc'
    : STAGE_AT[record.status] ?? 'outcome';

  return STAGES.map((stage) => {
    const drawn = { key: stage.key, label: stage.label, sheet: stage.sheet ?? null, title: null };
    if (stage.key === 'prospect') return { ...drawn, state: 'done' };

    if (stage.key === 'outcome') {
      if (record.status === 'APPROVED') {
        return {
          ...drawn,
          state: 'done',
          label: byException(record) ? 'Approved by exception' : 'Approved',
        };
      }
      if (record.status === 'REJECTED') return { ...drawn, state: 'rejected', label: 'Rejected' };
      return { ...drawn, state: 'next' };
    }

    if (stage.key === 'credit' && skipped && stage.key !== at) {
      return { ...drawn, state: 'skipped', title: skipNote };
    }
    if (record.status === 'APPROVED' || before(stage.key, at)) return { ...drawn, state: 'done' };
    if (stage.key === at) {
      if (record.status === 'REJECTED') return { ...drawn, state: 'rejected' };
      if (record.status === 'OVERRIDE_REQUESTED') {
        return { ...drawn, state: 'warning', label: 'Rejected, referred' };
      }
      return { ...drawn, state: 'current' };
    }
    return { ...drawn, state: 'next' };
  });
}

// The stage whose sheet opens when the address names none: the current one, for its owner.
export function defaultStage(record, me) {
  const stage = STAGE_AT[record.status];
  const owner = GATE_OWNERS[record.status];
  return stage && owner && me?.roles?.includes(owner) ? stage : null;
}

// The open stage, as the address names it: a sheet's stage, `none` for every sheet closed, or
// nothing - and then the default.
export const CLOSED = 'none';

export function stageFrom(searchParams, record, me) {
  const named = searchParams.get('stage');
  if (named === CLOSED) return null;
  return SHEET_STAGES.includes(named) ? named : defaultStage(record, me);
}

// The address with `stage` open, or every sheet closed; what else it holds - the tab - is kept.
export function stageSearch(searchParams, stage) {
  const next = new URLSearchParams(searchParams);
  next.set('stage', stage ?? CLOSED);
  return `?${next}`;
}

const NOTHING = Object.freeze({
  approve: null,
  returnTo: [],
  reject: false,
  refer: false,
  grant: false,
  endOverride: false,
  reopen: false,
});

// What the person looking may do, by the status: offered to the owner of the current stage and
// nobody else; an approved counterparty offers nothing. Compliance's approval is named by where
// the KYC review sends it. Legal's rejection may be referred to senior management (`refer`), and
// a referred one is answered by their grant or their decline (ADR-0014).
export function actionsFor(record, me, kycSheet) {
  const owner = GATE_OWNERS[record.status];
  if (!owner || !me?.roles?.includes(owner)) return NOTHING;
  switch (record.status) {
    case 'PROSPECT': {
      const toLegal = kycSheet?.credit_review === 'NOT_REQUIRED';
      return {
        ...NOTHING,
        approve: toLegal
          ? { target: 'LEGAL_REVIEW', label: 'Approve KYC → Legal' }
          : { target: 'KYC_APPROVED', label: 'Approve KYC → Credit' },
        reject: true,
      };
    }
    case 'KYC_APPROVED':
      return {
        ...NOTHING,
        approve: { target: 'LEGAL_REVIEW', label: 'Approve credit → Legal' },
        returnTo: ['PROSPECT'],
        reject: true,
      };
    case 'LEGAL_REVIEW':
      return {
        ...NOTHING,
        approve: { target: 'APPROVED', label: 'Approve' },
        returnTo: ['PROSPECT', 'KYC_APPROVED'],
        reject: true,
        refer: true,
      };
    case 'OVERRIDE_REQUESTED':
      return { ...NOTHING, grant: true, endOverride: true };
    case 'REJECTED':
      return { ...NOTHING, reopen: true };
    default:
      return NOTHING;
  }
}

// Whoever passed one gate on a counterparty passes no other (ADR-0014): the API refuses them, so
// the approval is not offered to them.
export const passedEarlierGate = (record, me) => Boolean(me?.email) && [
  record.kyc_approved_by, record.credit_approved_by,
].includes(me.email.toLowerCase());

// The sheet behind each approval, and how the hint names it. Legal's waits on nothing.
const GATES = {
  PROSPECT: { sheet: 'KYC review', key: 'kyc', labels: kycMissing, kind: 'kyc_incomplete' },
  KYC_APPROVED: {
    sheet: 'credit assessment', key: 'credit', labels: creditMissing, kind: 'assessment_incomplete',
  },
};

// What the approval at `status` is waiting on, as last saved: the sheet's name and what it lacks.
export function waitingOn(status, sheets) {
  const gate = GATES[status];
  if (!gate) return { sheet: null, missing: [] };
  return { sheet: gate.sheet, missing: gate.labels(sheets[gate.key]?.missing_for_approval) };
}

// The override's refusals, in the screen's words.
const OVERRIDE_REFUSALS = {
  override_approvers_missing: 'No override approver is configured, so the request would email nobody.',
  override_approver_unknown: 'Name one of the approvers the request was sent to.',
  override_evidence_missing:
    'Choose the written reply: a document attached to this counterparty after the request.',
};

// The API's refusal of a move, in the screen's words; null when it is not one of these.
export function refusalMessage(detail) {
  const gate = Object.values(GATES).find((g) => g.kind === detail?.kind);
  if (gate) return `The ${gate.sheet} needs: ${gate.labels(detail.missing).join(', ')}.`;
  return OVERRIDE_REFUSALS[detail?.kind] ?? null;
}

const STALE = 'This counterparty changed since you opened it. Reload to see its current status.';

// What a refused move on the path says: the refusal in the screen's words, a stale counterparty,
// or the API's message.
export const moveRefusal = (err) =>
  refusalMessage(err.detail) ?? (err.status === 409 ? STALE : err.message);

// The documents a grant may cite: attached, and attached after Legal asked.
export const evidenceAmong = (documents, override) => documents.filter(
  (document) => override && new Date(document.created_at) > new Date(override.requested_at),
);
